import { state, updateCurrentRoute } from '../lib/state.js';
import { saveCalculationResult, saveCalculationError, hydrateCalculationResultScreenshot } from '../services/repository.js';
import { els } from '../app/dom.js';
import { requestRender } from '../app/events.js';
import { reloadRoutes } from '../routes/route-data.js';
import { setStatus, esc, finiteNumberOrNull, pickCalculatorScreenshot, sectionDistanceKm, formatDuration } from '../lib/app-helpers.js';
import { calculatorMeta } from './registry.js';
import { serverCalculatorLabel, browserlessCalculatorLabel } from './common.js';

export async function handleCalculatorMessage(message){
  if(message.type==='JOB_ACCEPTED'){
    if(message.transport==='server'){
      const serverLabel=serverCalculatorLabel(message.payload?.calculatorKey);
      return setStatus(els.extensionStatus,`Zadanie ${serverLabel} zostało wysłane na serwer i oczekuje na uruchomienie.`,'working');
    }
    if(message.transport==='browserless'){
      const label=browserlessCalculatorLabel(message.payload?.calculatorKey);
      return setStatus(els.extensionStatus,`${label} zostało wysłane do Browserless.`,'working');
    }
    return;
  }
  if(message.type==='JOB_STATUS'&&message.transport==='browserless'){
    const label=browserlessCalculatorLabel(message.payload?.calculatorKey);
    return setStatus(els.extensionStatus,`Browserless uruchomił ${label}. Trwa obliczenie ceny i przygotowanie zrzutu…`,'working');
  }
  if(message.type==='JOB_STATUS'&&message.transport==='server'){
    const status=message.payload?.status;
    const serverLabel=serverCalculatorLabel(message.payload?.calculatorKey);
    const text=status==='processing'
      ? `Serwer rozpoczął obliczanie ${serverLabel}. Trwa wprowadzanie punktów i przygotowanie zrzutu…`
      : `Zadanie ${serverLabel} oczekuje w kolejce serwera…`;
    return setStatus(els.extensionStatus,text,'working');
  }
  if(message.type==='JOB_ERROR'){
    const serverLabel=serverCalculatorLabel(message.payload?.calculatorKey);
    const incomingError=message.payload||{};
    const errorSection=state.currentRoute.sections.find(s=>s.id===incomingError.sectionId||s.order===incomingError.sectionOrder);
    if(errorSection&&state.currentRoute?.id){
      saveCalculationError(state.currentRoute,errorSection,{
        ...incomingError,
        calculatorKey:incomingError.calculatorKey||errorSection.calculatorKey,
        source:message.transport==='server'?'oracle':'browserless',
        errorMessage:message.error||(message.transport==='server'?`Błąd serwera ${serverLabel}.`:`Błąd Browserless ${browserlessCalculatorLabel(incomingError.calculatorKey||errorSection?.calculatorKey)}.`),
        calculatedAt:new Date().toISOString(),
      }).then(()=>window.dispatchEvent(new CustomEvent('d24:calculation-saved'))).catch(()=>{});
    }
    return setStatus(els.extensionStatus,message.error||(message.transport==='server'?`Błąd serwera ${serverLabel}.`:`Błąd Browserless ${browserlessCalculatorLabel(incomingError.calculatorKey||errorSection?.calculatorKey)}.`),'error');
  }
  if(!['CALCULATOR_RESULT','ETOLL_RESULT'].includes(message.type))return;

  const rawIncoming=message.payload||{};
  let incoming=rawIncoming;
  if(message.transport==='server'&&rawIncoming?.screenshotPath&&!pickCalculatorScreenshot(rawIncoming)){
    try{
      incoming=await hydrateCalculationResultScreenshot(rawIncoming);
    }catch(error){
      console.warn('[D24] Nie udało się od razu pobrać zrzutu Oracle:',error);
    }
  }
  const section=state.currentRoute.sections.find(s=>s.id===incoming.sectionId||s.order===incoming.sectionOrder);
  if(!section)return;
  const calculatorScreenshot=pickCalculatorScreenshot(incoming);
  const calculatedDistanceKm=sectionDistanceKm(section);
  const calculatedDuration=Number.isFinite(Number(section.durationSeconds))?formatDuration(Number(section.durationSeconds)):'';
  const result={
    ...incoming,
    calculatorKey:incoming.calculatorKey||section.calculatorKey,
    distanceKm:calculatedDistanceKm??finiteNumberOrNull(incoming.distanceKm),
    distanceSource:calculatedDistanceKm!==null?'OSRM – trasa wyznaczona w aplikacji':(incoming.distanceSource||''),
    durationText:calculatedDuration||incoming.durationText||'',
    calculatorScreenshot,
    screenshotDataUrl:calculatorScreenshot||incoming.screenshotDataUrl||'',
    source:message.transport==='server'?'oracle':'browserless',
    calculatedAt:incoming.calculatedAt||incoming.completedAt||new Date().toISOString(),
  };
  const routeWasDirty=Boolean(state.currentRoute.dirty);
  const results={...state.currentRoute.results,[section.id]:result};
  // Sam wynik kalkulatora zapisuje się osobno w calculation_results i historii.
  // Nie jest zmianą przebiegu linii i nie powinien wymuszać pełnego saveRouteSnapshot.
  updateCurrentRoute({results,dirty:routeWasDirty});
  const meta=calculatorMeta(result.calculatorKey)||{name:result.calculatorName||'kalkulator'};
  const screenshotInfo=calculatorScreenshot?' Zrzut zapisano do raportu.':` Brak zrzutu${result.screenshotError?`: ${result.screenshotError}`:'.'}`;
  setStatus(els.extensionStatus,`Odebrano wynik ${meta.name}: ${result.costText||'brak ceny'}. Dystans trasy: ${result.distanceKm??'brak'} km.${screenshotInfo}`,calculatorScreenshot?'ok':'warning');
  try{
    await saveCalculationResult(state.currentRoute,section,result);
    window.dispatchEvent(new CustomEvent('d24:calculation-saved'));
    if(state.currentRoute.id){
      await reloadRoutes();
    }
    setStatus(
      els.extensionStatus,
      `Odebrano i zapisano wynik ${meta.name}: ${result.costText||'brak ceny'}. Dystans trasy: ${result.distanceKm??'brak'} km.${screenshotInfo}`,
      calculatorScreenshot?'ok':'warning'
    );
  }catch(error){
    console.warn('[D24] Automatyczny zapis wyniku kalkulatora nie powiódł się:',error);
    setStatus(
      els.extensionStatus,
      `Odebrano wynik ${meta.name}, ale nie udało się zapisać go automatycznie w Supabase: ${error.message}`,
      'error'
    );
  }
  requestRender();
}

export function renderResults(){
  els.resultsList.textContent='';
  const entries=Object.entries(state.currentRoute.results||{});
  if(!entries.length){els.resultsList.innerHTML='<div class="empty">Brak wyników kalkulatorów.</div>';return;}
  entries.forEach(([id,r])=>{
    const s=state.currentRoute.sections.find(x=>x.id===id);
    const card=document.createElement('article');
    card.className='result-card';
    const screenshot=pickCalculatorScreenshot(r);
    const mytoResultScreenshot=String(r?.mytoResultScreenshotDataUrl||'');
    const mytoMapScreenshot=String(r?.mytoMapScreenshotDataUrl||'');
    const isMyto=String(r.calculatorKey||s?.calculatorKey||'')==='myto-cz';
    const distance=r.distanceKm??sectionDistanceKm(s)??'—';
    const meta=calculatorMeta(r.calculatorKey||s?.calculatorKey)||{name:r.calculatorName||'Kalkulator'};
    const settings=r.settingsSummary||r.vehicleSettingsText||'';
    const mytoScreens=isMyto&&(mytoResultScreenshot||mytoMapScreenshot)
      ? `<div class="myto-two-screens">
          ${mytoResultScreenshot?`<figure><figcaption>MYTO CZ — wynik</figcaption><img class="result-image" src="${mytoResultScreenshot}" alt="MYTO CZ wynik"></figure>`:''}
          ${mytoMapScreenshot?`<figure><figcaption>MYTO CZ — mapa trasy</figcaption><img class="result-image" src="${mytoMapScreenshot}" alt="MYTO CZ mapa trasy"></figure>`:''}
        </div>`
      : '';
    card.innerHTML=`<h3>${esc(s?.displayName||s?.countryName||r.countryName||'Wynik')} — ${esc(meta.name)}</h3><div class="section-meta">Koszt: <strong>${esc(r.costText||'—')}</strong><br>Dystans wyznaczonego odcinka: ${distance} km<br>Czas: ${esc(r.durationText||'—')}${settings?`<br>Parametry: ${esc(settings)}`:''}</div>${mytoScreens|| (screenshot?`<img class="result-image" src="${screenshot}" alt="Zrzut kalkulatora">`:'<div class="status warning">Brak zrzutu z kalkulatora.</div>')}`;
    els.resultsList.appendChild(card);
  });
}

